import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BoyGate, useBoyInfo } from "@/components/boy/BoyShell";
import { StepBar } from "@/components/boy/courier/StepBar";
import { StepBasic } from "@/components/boy/courier/StepBasic";
import { StepVehicle } from "@/components/boy/courier/StepVehicle";
import { StepRegion } from "@/components/boy/courier/StepRegion";
import { StepDocs } from "@/components/boy/courier/StepDocs";
import { StepBank } from "@/components/boy/courier/StepBank";
import { Waiting } from "@/components/boy/courier/Waiting";
import { api, errMsg } from "@/lib/api";
import { EMPTY, payloadOf } from "@/lib/boyCourier";

const KEY = (code) => `boy_courier_signup_${code}`;
const STEPS = [StepBasic, StepVehicle, StepRegion, StepDocs];
const strip = (f) => Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v?.preview ? { ...v, preview: "" } : v]));

function load(code) {
  try {
    const s = JSON.parse(sessionStorage.getItem(KEY(code)) || "{}");
    return { step: 0, done: false, ...s, f: { ...EMPTY, ...(s.f || {}) } };
  } catch {
    return { step: 0, done: false, f: EMPTY };
  }
}

export default function BoyCourierSignup() {
  const { code } = useParams();
  const [info, err] = useBoyInfo(code);
  const [s, setS] = useState(() => load(code));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    sessionStorage.setItem(KEY(code), JSON.stringify({ ...s, f: strip(s.f) }));
  }, [s, code]);
  const patch = (p) => setS((x) => ({ ...x, f: { ...x.f, ...p } }));
  const go = (step) => {
    setS((x) => ({ ...x, step }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/public/boy/${code}/couriers`, payloadOf(s.f));
      setS({ step: 0, done: true, f: { ...EMPTY, phone: s.f.phone } });
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  const { step, f } = s;
  const Step = STEPS[step];
  const props = { f, patch, code, info, onNext: () => go(step + 1) };
  return (
    <BoyGate info={info} err={err}>
      {s.done ? <Waiting phone={f.phone} /> : (
        <div className="space-y-5" data-testid="boy-courier-signup">
          <StepBar step={step} onBack={() => go(step - 1)} />
          <div key={step} className="fade-up card-surface p-5 sm:p-6">
            {Step ? <Step {...props} /> : <StepBank {...props} busy={busy} onSubmit={submit} />}
          </div>
        </div>
      )}
    </BoyGate>
  );
}
